import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Sparkles, 
  BookOpen, 
  Compass, 
  TrendingUp, 
  CheckCircle2, 
  ArrowRight,
  Video,
  ExternalLink,
  History
} from 'lucide-react';
import LazyVideoPlayer from '../components/common/LazyVideoPlayer';
import { useLanguage } from '../context/LanguageContext';

const DrLightCaseStudy: React.FC = () => {
  const { language } = useLanguage();

  const isEn = language === 'EN';

  const canonicalUrl = isEn
    ? "https://www.we24agency.com/en/dr-light"
    : "https://www.we24agency.com/dr-light";
  const alternateTr = "https://www.we24agency.com/dr-light";
  const alternateEn = "https://www.we24agency.com/en/dr-light";

  const magazineIssues = [
    {
      issue: isEn ? "Issue 07" : "Sayı: 07",
      date: isEn ? "March 2026" : "Mart 2026",
      title: isEn ? "DR-TREND — Issue #7" : "DR-TREND — Sayı: 7",
      coverImage: "/dr-trend/dr-trend-issue-7.jpg",
      link: "https://www.dr-light.com/magazine/mar-2026",
      desc: isEn 
        ? "Latest architectural lighting trends, bespoke brass design concepts, and premier hospitality projects."
        : "Güncel mimari aydınlatma trendleri, pirinç zanaat detayları ve prestijli otel/konut projelerinin editoryal seçkisi."
    },
    {
      issue: isEn ? "Issue 06" : "Sayı: 06",
      date: isEn ? "February 2025" : "Şubat 2025",
      title: isEn ? "DR-TREND — Issue #6" : "DR-TREND — Sayı: 6",
      coverImage: "/dr-trend/dr-trend-issue-6.jpg",
      link: "https://www.dr-light.com/magazine/feb-2025",
      desc: isEn 
        ? "Sustainable lighting engineering, minimal spatial aesthetics, and interviews with leading interior architects."
        : "Sürdürülebilir aydınlatma mühendisliği, minimal mekân estetiği ve öncü iç mimarlarla röportajlar."
    },
    {
      issue: isEn ? "Issue 05" : "Sayı: 05",
      date: isEn ? "December 2023" : "Aralık 2023",
      title: isEn ? "DR-TREND — Issue #5" : "DR-TREND — Sayı: 5",
      coverImage: "/dr-trend/dr-trend-issue-5.jpg",
      link: "https://www.dr-light.com/magazine/dec-2023",
      desc: isEn 
        ? "Winter lighting selections, warm color temperatures, and modern manufacturing innovations."
        : "Kış koleksiyonu aydınlatma seçkisi, sıcak ışık renk sıcaklıkları ve modern üretim inovasyonları."
    },
    {
      issue: isEn ? "Issue 04" : "Sayı: 04",
      date: isEn ? "May 2023" : "Mayıs 2023",
      title: isEn ? "DR-TREND — Issue #4" : "DR-TREND — Sayı: 4",
      coverImage: "/dr-trend/dr-trend-issue-4.jpg",
      link: "https://www.dr-light.com/magazine/may-2023",
      desc: isEn 
        ? "The inaugural edition designed by WE24 Agency, redefining the visual typography and layout standards of the magazine."
        : "WE24 Agency tarafından editoryal mizanpaj ve tipografi standartları baştan tasarlanan ilk DR-TREND sayısı."
    }
  ];

  const timelineData = [
    {
      year: "2017",
      title: isEn ? "Foundation & First Brand Films" : "Temeller ve İlk Marka Filmleri",
      desc: isEn 
        ? "Produced the first bilingual (TR/EN) corporate brand film, 2 cinematic teaser films, and 4 in-depth product journey stories showcasing handcrafting and precision brass casting."
        : "Dr-Light markasının ilk Türkçe ve İngilizce kurumsal tanıtım filmi, 2 adet sinematik teaser ve el işçiliği ile pirinç döküm süreçlerini anlatan 4 adet ürün hikayesi filmi çekildi.",
      badge: isEn ? "2017 • Archive Milestone" : "2017 • İlk Dönem Arşivi",
      items: isEn 
        ? ["Bilingual Corporate Film (TR/EN)", "2x Product Teasers", "4x Craftsmanship Stories"]
        : ["Çift Dilli Tanıtım Filmi (TR/EN)", "2x Ürün Teaser Filmi", "4x Ürün Hikayesi Videosu"]
    },
    {
      year: "2018",
      title: isEn ? "Slimtech Launch & Mimarsiv Selection" : "Slimtech Lansmanı & Mimarsiv Selection",
      desc: isEn
        ? "Captured the 'Sound of Dr-Light' live rhythm performance at Mimarsiv 2018. Produced the flagship Slimtech commercial film, launch aftermovie, and 5 technical installation guides."
        : "Mimarsiv 2018'de gerçekleştirilen 'Sound of Dr-Light' özel performansının etkinlik filmleri, Slimtech serisi ürün reklam filmi, lansman aftermovie ve 5 adet kurulum eğitim videosu tamamlandı.",
      badge: isEn ? "2018 • Launch Direction" : "2018 • Lansman Filmleri",
      items: isEn
        ? ["Sound of Dr-Light Event Video", "Slimtech Commercial Film", "5x Technical Installation Guides"]
        : ["Sound of Dr-Light Etkinlik Filmi", "Slimtech Reklam Filmi & Aftermovie", "5x Ürün Kurulum Filmi"]
    },
    {
      year: "2019",
      title: isEn ? "Integrated Factory & Foundry Film" : "Entegre Fabrika & Üretim Filmi",
      desc: isEn
        ? "Produced a comprehensive industrial factory film spotlighting local manufacturing power, high-tech automated machining, and master artisan finishing lines."
        : "Dr-Light'ın yerli üretim gücünü, yüksek teknolojili talaşlı imalat atölyelerini ve zanaatkar cila/montaj süreçlerini belgeleyen kapsamlı fabrika tanıtım filmi hayata geçirildi.",
      badge: isEn ? "2019 • Industrial Film" : "2019 • Fabrika Tanıtım Filmi",
      items: isEn
        ? ["Full Factory Showcase", "CNC & Brass Workshop Cuts", "International B2B Master"]
        : ["Fabrika Tanıtım Master Filmi", "CNC & Pirinç Atölye Kesitleri", "Uluslararası B2B Dağıtım"]
    },
    {
      year: "2022",
      title: isEn ? "Digital Ecosystem & Nationwide Billboards" : "360° Dijital Yönetim & Billboard Kampanyası",
      desc: isEn
        ? "Took full command of 360° social media management and performance advertising. Directed cinematic commercial spots broadcast across nationwide digital billboard networks."
        : "Markanın tüm sosyal medya hesap yönetimi, içerik stratejisi ve performans reklamları ajansımızca devralındı. Türkiye genelinde LED billboardlarda dönen dinamik reklam filmleri üretildi.",
      badge: isEn ? "2022 • 360° Digital & OOH" : "2022 • Dijital & Açıkhava",
      items: isEn
        ? ["Social Media Account Growth", "Performance Ad Strategy", "Nationwide LED Billboard Ads"]
        : ["Sosyal Medya Hesap Yönetimi", "Hedefli Dijital Reklamlar", "Türkiye Geneli Billboard Filmleri"]
    },
    {
      year: "2023",
      title: isEn ? "DR-TREND Magazine (Issues #4 & #5) & VR" : "DR-TREND Dergisi (4 & 5. Sayılar) & VR",
      desc: isEn
        ? "Took over the entire editorial layout and design for DR-TREND Magazine starting with Issue #4 and #5. Produced the first 360° Virtual Showroom."
        : "4. sayıdan itibaren DR-TREND dergisinin tüm editoryal mizanpaj ve tipografi tasarımı WE24 tarafından devralındı (4 ve 5. sayılar tamamlandı). İlk 360° Sanal Showroom projesi hayata geçirildi.",
      badge: isEn ? "2023 • Editorial & VR" : "2023 • Kreatif Yayıncılık & VR",
      items: isEn
        ? ["DR-TREND Issues #4 & #5 Design", "360° Virtual Showroom V1", "Architectural Product Photography"]
        : ["DR-TREND Dergisi 4 & 5. Sayı Tasarımı", "360° Sanal Showroom V1", "Mimari Aydınlatma Fotoğrafçılığı"]
    },
    {
      year: "2025",
      title: isEn ? "DR-TREND Issue #6 & Next-Gen 360° (dr-light.art)" : "DR-TREND 6. Sayı & Yenilenen 360° (dr-light.art)",
      desc: isEn
        ? "Designed the 6th edition of DR-TREND Magazine. Completely rebuilt the interactive 360° Virtual Showroom infrastructure with photorealistic spatial lighting at dr-light.art."
        : "DR-TREND dergisinin 6. sayısının tasarımı tamamlandı. Dr-Light 360° Sanal Showroom altyapısı fotogerçekçi ışık simülasyonlarıyla baştan aşağı yenilenerek dr-light.art adresinde yayına alındı.",
      badge: isEn ? "2025 • Editorial & VR" : "2025 • Yayıncılık & Sanal Tur",
      items: isEn
        ? ["DR-TREND Issue #6 Design", "Renewed 360° Showroom Platform (dr-light.art)", "Global Architectural Access"]
        : ["DR-TREND Dergisi 6. Sayı Tasarımı", "Yenilenen 360° Sanal Showroom (dr-light.art)", "Mekânsal Işık Simülasyonu"]
    },
    {
      year: "2026",
      title: isEn ? "DR-TREND Issue #7 & Ongoing Leadership" : "DR-TREND 7. Sayı & Sürekli Liderlik",
      desc: isEn
        ? "Executed the full editorial curation and typography for the 7th edition of DR-TREND Magazine, solidifying Dr-Light's authority among top architects and global designers."
        : "DR-TREND dergisinin 7. sayısının mizanpaj ve görsel kürasyonu tamamlandı; Dr-Light'ın mimarlık ve aydınlatma dünyasındaki lider marka otoritesi pekiştirildi.",
      badge: isEn ? "2026 • Issue #7" : "2026 • 7. Sayı Yayında",
      items: isEn
        ? ["DR-TREND Issue #7 Design", "Omnichannel Brand Direction", "Architectural B2B Reach"]
        : ["DR-TREND Dergisi 7. Sayı Tasarımı", "Kesintisiz Dijital İletişim", "B2B Mimari Ağ"]
    }
  ];

  const pillars = [
    {
      icon: BookOpen,
      title: isEn ? "Editorial Design: DR-TREND Magazine (Issues 4-7)" : "Kreatif Yayıncılık: DR-TREND Dergisi (Sayı 4-7)",
      desc: isEn
        ? "From Issue #4 to #7, complete editorial curation, bespoke typography, and luxury print production for DR-TREND Magazine, reaching thousands of premier architects and interior designers."
        : "4. sayıdan itibaren 7. sayıya kadar tüm DR-TREND dergilerinin editoryal mizanpaj, görsel seçki, tipografi ve baskı öncesi hazırlık süreçleri ajansımızca titizlikle yönetilmektedir."
    },
    {
      icon: Compass,
      title: isEn ? "360° Virtual Showroom (Renewed 2025)" : "360° Sanal Showroom (2025'te Yenilendi)",
      desc: isEn
        ? "Next-gen 360-degree spatial scanning and digital showroom experience at dr-light.art, allowing architects and interior designers worldwide to explore collections in true light fidelity."
        : "2025'te dr-light.art adresinde baştan aşağı yenilenen sanal showroom altyapısı, koleksiyonların birebir ışık sıcaklıkları ve mekânsal etkileriyle gezilebilmesini sağlar."
    },
    {
      icon: Video,
      title: isEn ? "Cinematic Video Production" : "Sinematik Video Prodüksiyon",
      desc: isEn
        ? "From initial 2017 foundation brand films to Slimtech commercials, factory films, and OOH billboard videos, all filmed in 4K with custom lighting setups reflecting luxury interior warmth."
        : "2017'deki ilk tanıtım filminden Slimtech serisi reklamlarına, fabrika tanıtımından açıkhava billboard videolarına kadar tüm yapımlar ışığın zarafetini yansıtan 4K sinematografiyle üretildi."
    },
    {
      icon: TrendingUp,
      title: isEn ? "Social Media & Performance Ads" : "Sosyal Medya & Dijital Reklam Yönetimi",
      desc: isEn
        ? "Strategic Instagram & LinkedIn curation, architectural aesthetic content creation, and hyper-targeted advertising campaigns connecting with elite architects and B2B buyers."
        : "Aydınlatma estetiğini öne çıkaran sosyal medya içerikleri, mimari fotoğrafçılık ve seçkin mimarlık ofislerine yönelik B2B dijital reklam kampanyalarıyla marka bilinirliği katlandı."
    }
  ];

  return (
    <div className="bg-[#030811] text-foreground min-h-screen">
      <Helmet>
        <title>{isEn ? "Dr-Light & WE24: 8+ Years of Digital Transformation Case Study (2017-2026) - WE24 Agency" : "Dr-Light & WE24: 8+ Yıllık 360° Dijital Dönüşüm Vaka Analizi (2017-2026) - WE24 Agency"}</title>
        <meta 
          name="description" 
          content={isEn 
            ? "Discover how WE24 Agency and luxury architectural lighting pioneer Dr-Light built an 8+ year continuous partnership spanning DR-TREND magazine (Issues 4-7), archive brand films, and the 2025 renewed 360° virtual showroom (dr-light.art)."
            : "Lüks mimari aydınlatma öncüsü Dr-Light ile WE24 Agency'nin 2017'den bu yana süren 8+ yıllık iş ortaklığı: DR-TREND dergisi (4, 5, 6 ve 7. sayılar), arşiv filmleri ve 2025'te yenilenen 360° sanal showroom (dr-light.art)."
          } 
        />
        <meta name="keywords" content="Dr-Light, WE24 Agency, DR-TREND Dergisi, Mimari Aydınlatma, 360 Sanal Showroom, dr-light.art, Vaka Analizi, Editoryal Tasarım, Video Prodüksiyon" />
        <meta name="author" content="WE24 Agency" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="tr" href={alternateTr} />
        <link rel="alternate" hrefLang="en" href={alternateEn} />
        <link rel="alternate" hrefLang="x-default" href={alternateTr} />

        {/* OpenGraph */}
        <meta property="og:title" content={isEn ? "Dr-Light & WE24: 8+ Years Digital Transformation Case Study" : "Dr-Light & WE24: 8+ Yıllık 360° Dijital Dönüşüm Vaka Analizi"} />
        <meta property="og:description" content={isEn 
          ? "8+ years of excellence in DR-TREND magazine design (Issues 4-7), cinematic brand films, social media strategy, and the 2025 renewed virtual showroom."
          : "8+ yıllık iş birliğinde üretilen DR-TREND dergi tasarımları (4-7. sayılar), kurumsal filmler ve 2025'te yenilenen sanal showroom başarıları."
        } />
        <meta property="og:image" content="https://www.we24agency.com/dr-trend/dr-trend-issue-7.jpg" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="WE24 Agency" />
        <meta property="og:locale" content={isEn ? "en_US" : "tr_TR"} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={isEn ? "Dr-Light & WE24: 8+ Years Case Study" : "Dr-Light & WE24: 8+ Yıllık Vaka Analizi"} />
        <meta name="twitter:description" content={isEn ? "8+ years of luxury lighting digital transformation." : "8+ yıllık lüks aydınlatma dijital dönüşüm yolculuğu."} />
        <meta name="twitter:image" content="https://www.we24agency.com/dr-trend/dr-trend-issue-7.jpg" />

        {/* Structured Data JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": isEn ? "Home" : "Ana Sayfa",
                    "item": isEn ? "https://www.we24agency.com/en" : "https://www.we24agency.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": isEn ? "Projects" : "Projeler",
                    "item": isEn ? "https://www.we24agency.com/en/projects" : "https://www.we24agency.com/projeler"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Dr-Light 360° Case Study",
                    "item": canonicalUrl
                  }
                ]
              },
              {
                "@type": "Article",
                "headline": isEn ? "Dr-Light & WE24: 8+ Years of Digital Transformation" : "Dr-Light & WE24: Aydınlatmanın Dijital Dönüşümünde 8+ Yıllık Başarı Hikayesi",
                "description": isEn 
                  ? "A comprehensive 360-degree case study on the 8+ year strategic partnership between Dr-Light and WE24 Agency."
                  : "Dr-Light ve WE24 Agency arasındaki 8+ yıllık 360 derece stratejik iş ortaklığının detaylı vaka analizi.",
                "image": "https://www.we24agency.com/dr-trend/dr-trend-issue-7.jpg",
                "author": {
                  "@type": "Organization",
                  "name": "WE24 Agency",
                  "url": "https://www.we24agency.com"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "WE24 Agency",
                  "url": "https://www.we24agency.com",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://www.we24agency.com/favicon.png"
                  }
                },
                "datePublished": "2024-01-15",
                "dateModified": "2026-08-27",
                "inLanguage": isEn ? "en" : "tr"
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 border-b border-white/[0.08] overflow-hidden bg-gradient-to-b from-[#061524] via-[#030811] to-[#030811]">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#ff7a63]/15 blur-[140px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {/* Back link in own row */}
          <div className="mb-8">
            <Link
              to={isEn ? "/en/projects" : "/projeler"}
              className="inline-flex items-center gap-2 text-xs font-bold text-zinc-400 hover:text-white uppercase tracking-widest transition-colors group py-1"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1 text-[#ff7a63]" />
              <span>{isEn ? "Back to Projects" : "Tüm Projelere Dön"}</span>
            </Link>
          </div>

          {/* Super Badge in own row */}
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ff7a63]/10 border border-[#ff7a63]/25 text-[#ff7a63] text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{isEn ? "360° Strategic Brand Partnership (2017 - 2026)" : "360° Stratejik Marka Ortaklığı Vaka Analizi (2017 - 2026)"}</span>
            </div>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15] max-w-5xl font-display">
            {isEn ? (
              <>
                Dr-Light & WE24: <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-200 to-[#ff7a63]">8+ Years of Excellence</span> in Lighting & Digital Growth
              </>
            ) : (
              <>
                Dr-Light & WE24: <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-200 to-[#ff7a63]">Aydınlatma ve Dijital Dönüşümde</span> 8+ Yıllık Öncü İş Birliği
              </>
            )}
          </h1>

          <p className="text-base sm:text-xl text-zinc-300 max-w-4xl mt-6 leading-relaxed">
            {isEn
              ? "Since 2017, WE24 Agency has served as Dr-Light's dedicated creative partner, managing end-to-end editorial design for DR-TREND Magazine (Issues 4 to 7), 4K commercial films, nationwide billboard campaigns, and the next-generation 360° virtual showroom (renewed in 2025 at dr-light.art)."
              : "2017 yılında başlayan Dr-Light ve WE24 Agency iş birliği; 4. sayıdan itibaren tüm DR-TREND dergilerinin editoryal mizanpaj ve tipografi tasarımını, ilk dönem sinematik marka filmlerini, billboard kampanyalarını ve 2025 yılında baştan aşağı yenilediğimiz 360° sanal showroom'u (dr-light.art) kapsayan 360 derecelik bir marka mirasına dönüştü."
            }
          </p>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="p-5 rounded-2xl bg-zinc-900/60 border border-white/10 backdrop-blur-sm">
              <span className="text-3xl sm:text-4xl font-extrabold text-white font-display block">8+ {isEn ? "Years" : "Yıl"}</span>
              <span className="text-xs text-zinc-400 uppercase tracking-wider mt-1 block font-medium">
                {isEn ? "2017 - 2026 Active Partnership" : "2017 - 2026 Kesintisiz Ortaklık"}
              </span>
            </div>
            <div className="p-5 rounded-2xl bg-zinc-900/60 border border-white/10 backdrop-blur-sm">
              <span className="text-3xl sm:text-4xl font-extrabold text-[#ff7a63] font-display block">4 {isEn ? "Issues" : "Sayı"}</span>
              <span className="text-xs text-zinc-400 uppercase tracking-wider mt-1 block font-medium">
                {isEn ? "DR-TREND Magazine (Issues 4-7)" : "DR-TREND Dergi Tasarımı (4-7)"}
              </span>
            </div>
            <div className="p-5 rounded-2xl bg-zinc-900/60 border border-white/10 backdrop-blur-sm">
              <span className="text-3xl sm:text-4xl font-extrabold text-white font-display block">15+</span>
              <span className="text-xs text-zinc-400 uppercase tracking-wider mt-1 block font-medium">
                {isEn ? "Archive Films & Commercials" : "Film & Reklam Prodüksiyonu"}
              </span>
            </div>
            <div className="p-5 rounded-2xl bg-zinc-900/60 border border-white/10 backdrop-blur-sm">
              <span className="text-3xl sm:text-4xl font-extrabold text-[#ff7a63] font-display block">2025</span>
              <span className="text-xs text-zinc-400 uppercase tracking-wider mt-1 block font-medium">
                {isEn ? "360° Showroom Renewed (dr-light.art)" : "360° Sanal Showroom Yenilendi"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 2025 NEW VIRTUAL SHOWROOM BANNER */}
      <section className="py-12 border-b border-white/[0.08] bg-gradient-to-r from-zinc-950 via-[#071927] to-zinc-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="p-8 sm:p-10 rounded-3xl bg-zinc-900/60 border-2 border-[#ff7a63]/40 shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-left max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#ff7a63]/15 text-[#ff7a63] text-xs font-black uppercase tracking-wider border border-[#ff7a63]/30">
                <Sparkles className="w-3.5 h-3.5" />
                {isEn ? "2025 Innovation Spotlight" : "2025 İnovasyon Başlığı"}
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
                {isEn 
                  ? "Next-Gen 360° Virtual Showroom Rebuilt in 2025" 
                  : "2025'te Baştan Aşağı Yenilenen 360° Sanal Showroom"
                }
              </h2>
              <p className="text-sm text-zinc-300 leading-relaxed">
                {isEn
                  ? "In 2025, WE24 completely re-engineered Dr-Light's spatial virtual showroom with photorealistic lighting rendering and ultra-fast web walkthroughs. Explore the live interactive experience at dr-light.art."
                  : "2025 yılında Dr-Light için geliştirdiğimiz 360° Sanal Showroom deneyimi en güncel fotogerçekçi mekânsal tarama ve ışık simülasyonu teknolojileriyle modernize edildi. dr-light.art üzerinden hemen deneyimleyin."
                }
              </p>
            </div>
            <div className="shrink-0">
              <a
                href="https://www.dr-light.art/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-[#ff7a63] text-zinc-950 hover:bg-[#ff8f7a] text-xs font-black tracking-wider rounded-full transition-all shadow-xl hover:scale-105 inline-flex items-center gap-2.5"
              >
                <span>
                  {isEn ? "EXPLORE " : "SANAL SHOWROOM'U AÇ "}
                  <span className="lowercase font-bold tracking-normal font-sans">(www.dr-light.art)</span>
                </span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DR-TREND MAGAZINE SHOWCASE SECTION */}
      <section className="py-16 md:py-24 border-b border-white/[0.08] bg-zinc-950/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-widest text-[#ff7a63] font-bold block mb-2">
              {isEn ? "Editorial Design & Publishing" : "Kreatif Yayıncılık & Mizanpaj"}
            </span>
            <h2 className="text-3xl sm:text-5xl font-extrabold text-white font-display">
              {isEn ? "DR-TREND Magazine Collection" : "DR-TREND Dergisi Tasarım Koleksiyonu"}
            </h2>
            <p className="text-sm sm:text-base text-zinc-300 mt-3 leading-relaxed">
              {isEn
                ? "Starting from Issue #4 to the newly published Issue #7, all DR-TREND magazines are designed by WE24 Agency, from typography layout to architectural curation and prepress."
                : "4. sayıdan itibaren 5, 6 ve güncel 7. sayıya kadar tüm DR-TREND dergilerinin mizanpaj, tipografi, editoryal içerik seçkisi ve baskı öncesi hazırlık süreçleri WE24 Agency tarafından gerçekleştirilmektedir."
              }
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {magazineIssues.map((mag, mIdx) => (
              <div 
                key={mIdx}
                className="group bg-zinc-900/40 border border-white/10 hover:border-[#ff7a63]/40 rounded-3xl p-5 flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 shadow-2xl"
              >
                <div>
                  {/* Book Cover Container with 3D Effect */}
                  <div className="aspect-[3/4] w-full rounded-2xl overflow-hidden bg-zinc-950 mb-5 relative shadow-[0_15px_35px_rgba(0,0,0,0.6)] border border-white/10">
                    <img 
                      src={mag.coverImage} 
                      alt={mag.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>

                  <div className="flex items-center justify-between text-xs text-zinc-400 mb-2">
                    <span className="font-bold text-white uppercase tracking-wider">{mag.issue}</span>
                    <span className="text-[#ff7a63] font-semibold">{mag.date}</span>
                  </div>

                  <h3 className="text-lg font-bold text-white group-hover:text-[#ff7a63] transition-colors leading-snug mb-2 font-display">
                    {mag.title}
                  </h3>

                  <p className="text-xs text-zinc-400 leading-relaxed line-clamp-3 mb-6">
                    {mag.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <a
                    href={mag.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 px-4 rounded-xl bg-white/5 hover:bg-[#ff7a63] hover:text-zinc-950 text-white text-xs font-bold uppercase tracking-wider transition-all inline-flex items-center justify-center gap-2 group/btn"
                  >
                    <span>{isEn ? "Read Online (dr-light.com)" : "Dergiyi Oku (Online Reader)"}</span>
                    <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-0.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Video Archive Section */}
      <section className="py-16 md:py-24 border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-widest text-[#ff7a63] font-bold block mb-2">
              {isEn ? "Historical Video Archive (2017 - 2018)" : "Tarihsel Video Arşivi (2017 - 2018)"}
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold text-white font-display">
              {isEn ? "Milestones of Brand Cinematography" : "İş Ortaklığının İlk Dönem Video Yapımları"}
            </h2>
            <p className="text-sm sm:text-base text-zinc-400 mt-3">
              {isEn
                ? "Archived milestone productions produced by WE24 Agency during the brand's foundational visual identity era."
                : "Dr-Light'ın görsel kimlik dönüşümünün temellerini atan 2017 ve 2018 yılı kurumsal film ve lansman teaser yapımları."
              }
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Video 1: Kurumsal Tanıtım Filmi (2017) */}
            <div className="bg-zinc-900/40 border border-white/10 rounded-3xl p-6 flex flex-col justify-between relative group">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-zinc-300 text-[11px] font-bold uppercase tracking-wider border border-white/10">
                    <History className="w-3 h-3 text-[#ff7a63]" />
                    {isEn ? "2017 Production (Archive Milestone)" : "2017 Prodüksiyonu (Kurumsal Arşiv)"}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mt-1 mb-4">
                  {isEn ? "Dr-Light - First Corporate Brand Film (2017)" : "Dr-Light - İlk Kurumsal Tanıtım Filmi (2017)"}
                </h3>
              </div>
              <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 mb-4 bg-zinc-950">
                <LazyVideoPlayer
                  title="Dr-Light Kurumsal Tanıtım Filmi (2017)"
                  videoUrl="https://www.youtube.com/watch?v=8hRbpPgFXoo"
                  provider="youtube"
                  coverImage="/project-covers/dr-light-kurumsal-tanitim-filmi.webp"
                />
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                {isEn 
                  ? "Filmed in 2017: 4K HDR cinematography capturing artisanal brass finishes, precision engineering, and luxury interior light distribution."
                  : "2017 Yılı Yapımı: Pirinç el işçiliği, hassas metal işleme ve lüks mekân aydınlatma dağılımını vurgulayan ilk kurumsal tanıtım filmi."
                }
              </p>
            </div>

            {/* Video 2: Sosyal Medya Teaser Filmleri (2018) */}
            <div className="bg-zinc-900/40 border border-white/10 rounded-3xl p-6 flex flex-col justify-between relative group">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-zinc-300 text-[11px] font-bold uppercase tracking-wider border border-white/10">
                    <History className="w-3 h-3 text-[#ff7a63]" />
                    {isEn ? "2018 Production (Launch Archive)" : "2018 Prodüksiyonu (Lansman Arşivi)"}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mt-1 mb-4">
                  {isEn ? "Dr-Light - Social Media / Teaser Film (2018)" : "Dr-Light - Sosyal Medya / Teaser Filmi (2018)"}
                </h3>
              </div>
              <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 mb-4 bg-zinc-950">
                <LazyVideoPlayer
                  title="Dr-Light - Sosyal Medya / Teaser Filmi (2018)"
                  videoUrl="https://www.youtube.com/watch?v=3CoXmBylLHw"
                  provider="youtube"
                  coverImage="/project-covers/dr-light-sosyal-medya-teaser-filmleri.webp"
                />
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                {isEn 
                  ? "Filmed in 2018: Dynamic rhythm and dramatic macro close-ups produced for the digital launch of new architectural product collections."
                  : "2018 Yılı Yapımı: Yeni koleksiyonların dijital lansmanında mimarların dikkatini çekmek için hazırlanan dinamik ritimli teaser serisi."
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Pillars Section */}
      <section className="py-16 md:py-24 border-b border-white/[0.08] bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest text-[#ff7a63] font-bold block mb-2">
              {isEn ? "Strategic Capabilities" : "360° Hizmet Kapsamı"}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-display">
              {isEn ? "4 Strategic Pillars of Transformation" : "Dönüşümün 4 Temel Stratejik Ayağı"}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <div 
                  key={idx}
                  className="p-8 rounded-3xl bg-zinc-900/40 border border-white/10 hover:border-[#ff7a63]/40 transition-all group"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#ff7a63]/10 border border-[#ff7a63]/25 flex items-center justify-center text-[#ff7a63] mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{pillar.title}</h3>
                  <p className="text-sm text-zinc-300 leading-relaxed">{pillar.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Chronological Timeline */}
      <section className="py-16 md:py-24 border-b border-white/[0.08]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest text-[#ff7a63] font-bold block mb-2">
              {isEn ? "2017 - 2026 Road to Growth" : "2017 - 2026 Büyüme Yolculuğu"}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-display">
              {isEn ? "Chronology of an 8+ Year Collaboration" : "8+ Yıllık Başarı Kronolojisi"}
            </h2>
          </div>

          <div className="relative border-l-2 border-[#ff7a63]/30 pl-6 sm:pl-10 ml-4 sm:ml-8 space-y-12">
            {timelineData.map((item, idx) => (
              <div key={idx} className="relative group">
                {/* Timeline node dot */}
                <div className="absolute -left-[31px] sm:-left-[47px] top-1.5 w-5 h-5 rounded-full bg-[#030811] border-2 border-[#ff7a63] flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-[#ff7a63] group-hover:scale-150 transition-transform" />
                </div>

                <div className="p-6 sm:p-8 rounded-3xl bg-zinc-900/40 border border-white/10 hover:border-white/20 transition-all">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <span className="text-2xl font-black text-white font-display tracking-tight">{item.year}</span>
                    <span className="px-3 py-1 rounded-full bg-[#ff7a63]/10 text-[#ff7a63] border border-[#ff7a63]/20 text-[11px] font-bold uppercase tracking-wider">
                      {item.badge}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-4">{item.desc}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {item.items.map((sub, sIdx) => (
                      <span key={sIdx} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 bg-white/5 px-3 py-1 rounded-full border border-white/5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#ff7a63]" />
                        {sub}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-b from-transparent to-[#05131f]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white font-display">
            {isEn 
              ? "Ready to Build Your Brand's Growth Story?" 
              : "Markanız İçin Benzer Bir Başarı Hikayesi Başlatalım"
            }
          </h2>
          <p className="text-base text-zinc-300 mt-4 max-w-2xl mx-auto leading-relaxed">
            {isEn
              ? "From cinematic commercials to full omnichannel digital management and 360° spatial experiences, we partner with industry leaders to deliver lasting impact."
              : "Sinematik prodüksiyondan dergi tasarımına, 360° sanal showroom'dan dijital reklam stratejisine kadar markanızı tek çatı altında geleceğe taşıyalım."
            }
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to={isEn ? "/en/contact" : "/iletisim"}
              className="px-8 py-4 bg-white text-zinc-950 hover:bg-zinc-200 text-xs font-extrabold uppercase tracking-wider rounded-full transition-all shadow-xl hover:scale-105 inline-flex items-center gap-2"
            >
              {isEn ? "Start Your Project" : "Projenizi Başlatın"}
              <ArrowRight className="w-4 h-4 text-[#ff7a63]" />
            </Link>
            <a
              href="https://www.dr-light.art/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-zinc-900 border border-white/20 text-white hover:bg-zinc-800 text-xs font-extrabold uppercase tracking-wider rounded-full transition-all inline-flex items-center gap-2"
            >
              <span className="lowercase font-bold tracking-normal font-sans">www.dr-light.art</span>
              <ExternalLink className="w-3.5 h-3.5 text-[#ff7a63]" />
            </a>
            <a
              href="https://www.dr-light.com/magazine/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-zinc-900 border border-white/20 text-white hover:bg-zinc-800 text-xs font-extrabold uppercase tracking-wider rounded-full transition-all inline-flex items-center gap-2"
            >
              <span>{isEn ? "All Magazines" : "Tüm Dergiler"}</span>
              <BookOpen className="w-3.5 h-3.5 text-[#ff7a63]" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DrLightCaseStudy;
